import React, {useCallback, useState, useContext, useEffect} from "react";
import {useRequest} from "../../utils/hooks.jsx";
import {TableDateRangeTable} from "../../components/OrdersDateRangeTable/TableDateRangeTable.jsx";
import {
    briefPageSize,
    BriefStatusToColorProvider,
    providerBriefProjection,
    ProviderBriefsShortcuts,
    providerSelectedActions
} from "./consts.jsx";
import "./provider-briefs-table.css";
import {TableDateSearchContext} from "../../admin/Context/TableDateSearchContext.jsx";
import {AppContext} from "../../AppContext.jsx";
import {removeBriefFromProvider, cancelBid} from "../../api/briefs/briefs.js";
import {filterBriefsBySearchInput} from "./utils.jsx";
import {useHistory} from "react-router-dom";
import {columns} from "../../company/CompanyAccountProfile/CompanyBriefs/columns.jsx";
import {BriefStatus, tableColumns} from "../../company/CompanyAccountProfile/CompanyBriefs/consts.jsx";
import {isEmpty} from "lodash";
import {DeclineOrderModal} from "../../event/DeclineOrderModal/DeclineOrderModal.jsx";
import {
    briefProviderDeclineOptionsToText,
    PROVIDER_BRIEF_DECLINE_MODAL_HEADER,
    PROVIDER_BRIEF_DECLINE_MODAL_SUBTITLE
} from "../../event/DeclineOrderModal/consts.js";

export const ProviderBriefs = () => {
    const [currentPage, setCurrentPage] = useState(1);
    const [innerBriefs, setInnerBriefs] = useState([]);
    const [searchInput, setSearchInput] = useState(null);
    const {searchDate, searchByCreated, searchStatuses} = useContext(TableDateSearchContext);
    const {me} = useContext(AppContext);
    const [briefsDtstartOrder, setBriefsDtstartOrder] = useState(null);
    const [briefsCreatedAtOrder, setBriefsCreatedAtOrder] = useState(null);
    const [briefsSystemIdOrder, setBriefsSystemIdOrder] = useState([]);
    const [declineBriefModalVisible, setDeclineBriefModalVisible] = useState(null);

    const history = useHistory();

    const [myBriefs, isLoadingBriefs, error, fetchBriefs] = useRequest(
        "/api/providerBriefs",
        "POST",
        {
            page: currentPage,
            pageSize: briefPageSize,
            projection: providerBriefProjection,
            includeBids: true,
            startTime: searchDate[0].valueOf(),
            endTime: searchDate[1].valueOf(),
            byCreatedAt: searchByCreated,
            ...(!!searchInput && {searchInput: searchInput}),
            ...(searchStatuses?.length ? {statuses: searchStatuses} : {}),
            excludeHaveBidsWithProvider: true,
            ...(briefsDtstartOrder ? {orderBy: briefsDtstartOrder} : {}),
            ...(briefsCreatedAtOrder ? {orderBy: briefsCreatedAtOrder} : {}),
            ...(briefsSystemIdOrder?.length ? {systemIds: briefsSystemIdOrder} : {})
        },
        [
            currentPage,
            searchDate,
            searchByCreated,
            briefsDtstartOrder,
            briefsCreatedAtOrder,
            briefsSystemIdOrder,
            searchInput,
            searchStatuses
        ],
        true,
        null
    );

    const onCancelBid = useCallback(
        async brief => {
            const bid = brief?.bids?.find(b => b.providerId === me.userId);
            const bidId = bid?._id;

            const res = await cancelBid(brief.briefId, bidId);
            if (res?.error) {
                return;
            }
            fetchBriefs();
        },
        [fetchBriefs, me.userId]
    );

    const onDeclineBrief = useCallback(
        async declineDetails => {
            const res = await removeBriefFromProvider(
                declineBriefModalVisible.briefId,
                me.userId,
                declineDetails.declineReason,
                declineDetails.declineComment
            );
            if (res?.error) {
                return;
            }
            setDeclineBriefModalVisible(null);
            fetchBriefs();
        },
        [declineBriefModalVisible, fetchBriefs, me.userId]
    );

    const onViewBriefClick = useCallback(
        brief => {
            const bid = brief?.bids?.find(b => b.providerId === me.userId);
            history.push({
                pathname: `/dashboard/brief/${brief.briefId}`,
                state: {bidId: bid?.bidId}
            });
        },
        [history, me.userId]
    );

    const onOpenBidsClick = useCallback(
        brief => {
            const bid = brief?.bids?.find(b => b.providerId === me.userId);
            history.push({
                pathname: `/dashboard/brief/${brief.briefId}`,
                state: {bidId: bid?.bidId}
            });
        },
        [history, me.userId]
    );

    useEffect(() => {
        setInnerBriefs(myBriefs);
    }, [myBriefs]);

    useEffect(() => {
        const filteredBriefs = filterBriefsBySearchInput(myBriefs?.briefs, searchInput);
        setInnerBriefs(prev => ({...prev, briefs: filteredBriefs}));
    }, [searchInput, myBriefs]);

    return (
        <>
            <div className="provider-manage-invoices-title">ניהול בריפים</div>
            <TableDateRangeTable
                tableClassName="company-briefs"
                onInputSearch={setSearchInput}
                pagination={{
                    position: ["bottomRight"],
                    showSizeChanger: false,
                    current: currentPage,
                    defaultPageSize: briefPageSize,
                    total: innerBriefs?.briefs?.length,
                    showTotal: total => <span>סה״כ {total}</span>,
                    onChange: page => setCurrentPage(page)
                }}
                isLoadingOrderTable={isLoadingBriefs}
                shortcutSearchOptions={ProviderBriefsShortcuts}
                data={innerBriefs?.briefs ?? []}
                showSearchByCreatedAt={true}
                showDateRangeFilter={true}
                onChange={(_, filters) => {
                    if (filters?.systemId !== undefined) {
                        setBriefsSystemIdOrder(filters.systemId || []);
                    }
                }}
                columns={columns({
                    currentPage,
                    me,
                    briefPageSize,
                    briefStatusToColorMapper: BriefStatusToColorProvider,
                    setBriefsDtstartOrder,
                    briefsDtstartOrder,
                    setBriefsCreatedAtOrder,
                    briefsCreatedAtOrder,
                    setBriefsSystemIdOrder,
                    briefsSystemIdOrder,
                    manageBriefs: [
                        {
                            shouldShow: brief =>
                                brief?.status === BriefStatus.pending ||
                                (brief?.status === BriefStatus.haveBids &&
                                    brief?.bids?.every(bid => bid.providerId !== me.userId)),
                            text: "שליחת הצעה",
                            onClick: brief => history.push(`/dashboard/brief/${brief.briefId}`)
                        },
                        {
                            shouldShow: brief =>
                                brief?.status === BriefStatus.haveBids &&
                                brief?.bids?.some(bid => bid.providerId === me.userId && !isEmpty(bid.note)),
                            text: "שליחת תיקון",
                            onClick: onOpenBidsClick
                        }
                    ]
                })}
                hideColumns={[tableColumns.companyName, tableColumns.description, tableColumns.assignedCSMs]}
                actions={[
                    {
                        title: "מסיר השתתפות בהצעת מחיר",
                        onClick: brief => setDeclineBriefModalVisible(brief),
                        needConfirmation: false,
                        disabled: brief => brief?.status === BriefStatus.cancelled
                    },
                    {
                        title: "צפייה בבריף",
                        onClick: onViewBriefClick
                    },
                    {
                        title: "ביטול הצעה",
                        onClick: onCancelBid,
                        className: "provider-briefs-cancel-brief-btn",
                        needConfirmation: true,
                        disabled: brief =>
                            !brief?.bids?.some(bid => bid.providerId === me.userId) ||
                            brief?.status !== BriefStatus.haveBids
                                ? true
                                : false
                    }
                ]}
                disableFutureDates={false}
            />
            <DeclineOrderModal
                visible={!!declineBriefModalVisible}
                onSave={onDeclineBrief}
                onClose={() => setDeclineBriefModalVisible(null)}
                header={PROVIDER_BRIEF_DECLINE_MODAL_HEADER}
                subTitle={PROVIDER_BRIEF_DECLINE_MODAL_SUBTITLE}
                declineReasons={briefProviderDeclineOptionsToText}
                showWhoToNotify={false}
                declineReasonLabel="סיבת הסרת ההשתתפות"
                declineCommentLabel="ניתן להוסיף כאן תיאור של סיבת הסרת מועמדות"
            />
        </>
    );
};
